import {
  BookRequest,
  ClientKind,
  DiscountToggle,
  EditorChannel,
  EditorContextModel,
  EditorLine,
  EditorOrderModel,
  LineRef,
} from '@callcenter-app/service/order-editor.service';
import { lineWeight } from '@callcenter-app/util/variant';
import { cleanCompanyDraft, CompanyDraft } from './company';
import { toPhoneCountry } from './editor-phone';
import { cleanRejectionDraft, RejectionDraft } from './rejections';
import { CarriedTexts, DraftLine, DraftParty, DraftReceiving, DRAFT_TTL_MS, draftKey, newDraft, OrderDraft, parseDraft } from './order-draft';

/**
 * The draft of step 2 (design §5.3): what the form of a booking holds that the server does not — the texts (the inscription, the note, the comment), the switches («запрос в WhatsApp», the receipt,
 * the star, «сразу счёт Kaspi», «в долг»), the type of the client, the channel, «как узнал», the discounts the operator switched off — and what brings the operator back to step 1 when the
 * booking is taken off: the step-1 form as it was when the booking was made (`step1`, the very same `OrderDraft` the first page kept under `?d=`). The key is
 * `cc.orderEditor.<userId>.cart.<cartId>`: one operator, one booking, one tab (`sessionStorage`), 30 minutes since the last touch — the rules of the step-1 draft.
 *
 * The booking itself (the lines, the time, the address, the slot, the client, the files) is NOT here: the server keeps it, and every opening reads it (E12). A draft that is gone (a booking opened
 * from «Корзины», a new tab) is a blank form with the defaults — nothing is lost, the texts are only empty.
 *
 * Pure. Like the step-1 draft it is never put in `localStorage` or in the address of the page.
 */

export const BOOKED_DRAFT_VERSION = 1;

/** What the booking was made for — brand, city and day. The booking's own answer (E12) has all three; the draft keeps them so that «Забронировать заново» works after the booking is gone. */
export interface BookedScope {
  brandId: number;
  cityId: number;
  date: string;
}

export const TEXT_LIMIT = 2000;
export const REFERRER_LIMIT = 200;

/**
 * The details of a delivery address — «подъезд», «этаж», «кв.», «как найти» — as the operator wrote them on step 1 while the booking was held. They are NOT a new booking: the address and its point
 * are what the booking was made for, and these four lines are only text — so they live in the draft, step 2 shows them, and E21 takes them (`addressDetails`). Every field is a string, empty
 * when the operator cleared it (a block that is there replaces the details of the booking wholesale).
 */
export interface AddressDetails {
  entrance: string;
  floor: string;
  flat: string;
  note: string;
}

/** The longest entrance, floor and flat the server takes, and the longest «как найти». */
export const DETAIL_LIMIT = 50;
export const NOTE_LIMIT = 300;

/** The address with its details replaced by the operator's, when he wrote any; the address, the point and the district are the booking's. */
export function withAddressDetails<A extends { entrance?: string; floor?: string; flat?: string; note?: string }>(address: A, details: AddressDetails | undefined): A {
  if (!details) {
    return address;
  }
  const { entrance: _entrance, floor: _floor, flat: _flat, note: _note, ...rest } = address;
  return {
    ...(rest as A),
    ...(details.entrance ? { entrance: details.entrance } : {}),
    ...(details.floor ? { floor: details.floor } : {}),
    ...(details.flat ? { flat: details.flat } : {}),
    ...(details.note ? { note: details.note } : {}),
  };
}

/** The details a delivery of E11 (or the address of a booking) carries, as the draft keeps them. */
export function addressDetailsOf(address: { entrance?: string; floor?: string; flat?: string; note?: string }): AddressDetails {
  return { entrance: address.entrance?.trim() ?? '', floor: address.floor?.trim() ?? '', flat: address.flat?.trim() ?? '', note: address.note?.trim() ?? '' };
}

export interface BookedDraft {
  version: typeof BOOKED_DRAFT_VERSION;
  cartId: number;
  userId: number;
  /** Browser clock, ms: only the 30-minute term is measured with it. */
  touchedAt: number;
  /** The step-1 form of the conversation the booking was made in: «Снять бронь» and «Бронь снята → Закрыть» bring it back. Absent — the booking was opened from «Корзины». */
  step1?: OrderDraft;
  scope?: BookedScope;
  /** What E11 needs to book the same again («Забронировать заново»): taken from the answer of E12 every time it comes (with the details of the address the operator wrote since, below). */
  rebook?: BookRequest;
  /** The details of the delivery address the operator changed on step 1 while this very booking was held — over those of the booking (see `AddressDetails`). Absent: the booking's own. */
  addressDetails?: AddressDetails;
  decorText: string;
  /** «Попросить фото и пожелания в WhatsApp» — the request goes out when the order is made. */
  requestDecor: boolean;
  note: string;
  comment: string;
  channel: EditorChannel;
  referrer?: string;
  attachReceipt: boolean;
  important: boolean;
  /** «Сразу отправить счёт Kaspi» after the order is made. */
  kaspiNow: boolean;
  debt: boolean;
  clientType: ClientKind;
  /**
   * «Кто заказывает» was decided: by the operator, or by the booking itself — the kind E12 says, taken when the page was opened without a decision of its own (a booking made with a legal entity in
   * the old editor or in another window must not open as a person). Absent — nothing decided yet: the first reading of the booking decides.
   */
  clientTypeSet?: true;
  /** The discounts the operator switched off — `toggles` of E17 and E21, all `enabled: false`. */
  disabledDiscounts: DiscountToggle[];
  /** The forms of the legal entity as they are typed: the requisites, the letter of authority (2c). Absent — nothing typed. */
  company?: CompanyDraft;
  /** The refusals of the conversation (2c): the record of E24 and what it holds — written on either step. */
  rejection?: RejectionDraft;
  /**
   * The booking this one was made again from (E11 `replaceCartId`) held saved requisites of a legal entity: the server moves them to the new booking — unless the new store does not take the number
   * (another country) or the new booking has no client. The new page reads `company` of the booking and, if the requisites are not there, tells the operator to save them again; then the mark goes.
   */
  companyWas?: true;
}

export function newBookedDraft(cartId: number, userId: number, nowMs: number, init: Partial<Pick<BookedDraft, 'step1' | 'scope' | 'rebook' | 'disabledDiscounts' | 'rejection'>> = {}): BookedDraft {
  return {
    version: BOOKED_DRAFT_VERSION,
    cartId,
    userId,
    touchedAt: nowMs,
    decorText: '',
    requestDecor: false,
    note: '',
    comment: '',
    channel: 'CALLCENTER',
    attachReceipt: true,
    important: false,
    kaspiNow: false,
    debt: false,
    clientType: 'INDIVIDUAL',
    disabledDiscounts: [],
    ...(init.step1 ? { step1: init.step1 } : {}),
    ...(init.scope ? { scope: init.scope } : {}),
    ...(init.rebook ? { rebook: init.rebook } : {}),
    ...(init.disabledDiscounts ? { disabledDiscounts: init.disabledDiscounts } : {}),
    ...(init.rejection ? { rejection: init.rejection } : {}),
  };
}

export function bookedTouched(draft: BookedDraft, nowMs: number): BookedDraft {
  return { ...draft, touchedAt: nowMs };
}

export function isBookedExpired(draft: Pick<BookedDraft, 'touchedAt'>, nowMs: number): boolean {
  return nowMs - draft.touchedAt >= DRAFT_TTL_MS;
}

/** `cc.orderEditor.<userId>.cart.<cartId>`. */
export function bookedKey(userId: number, cartId: number): string {
  return draftKey(userId, 'cart', String(cartId));
}

// ---------- the booking made from the form of step 1, and the form made from the booking ----------

/**
 * The form without the texts of step 2 it carries and without the refusals of the conversation: the booking's own draft holds them as its fields (the refusals are written on either step, and the
 * newest state is the booking's), and the copy of the form in it is only for the way back.
 */
export function formWithoutCarried(form: OrderDraft): OrderDraft {
  const { step2: _carried, rejection: _rejection, ...rest } = form;
  return rest;
}

/** What step 2 holds that belongs to the conversation, to be carried to the next booking of it. */
export function carriedOf(draft: BookedDraft): CarriedTexts {
  return {
    decorText: draft.decorText,
    requestDecor: draft.requestDecor,
    note: draft.note,
    comment: draft.comment,
    channel: draft.channel,
    ...(draft.referrer ? { referrer: draft.referrer } : {}),
    attachReceipt: draft.attachReceipt,
    important: draft.important,
    kaspiNow: draft.kaspiNow,
    clientType: draft.clientType,
    ...(draft.clientTypeSet ? { clientTypeSet: true as const } : {}),
    ...(draft.company ? { company: draft.company } : {}),
  };
}

/** A draft with the texts of the conversation put into it. */
export function withCarried(draft: BookedDraft, carried: CarriedTexts | undefined): BookedDraft {
  if (!carried) {
    return draft;
  }
  const { referrer: _old, company: _oldCompany, clientTypeSet: _oldSet, ...rest } = draft;
  return { ...rest, ...carried };
}

/**
 * The draft of a booking made from step 1: the form travels under the key of the booking, with everything the booking needs to be made again — and the texts the operator wrote on step 2 of this
 * conversation before the booking before it was taken off, if there are any (they belong to the conversation).
 */
export function bookedDraftFromStep1(cartId: number, form: OrderDraft, scope: BookedScope, rebook: BookRequest, nowMs: number): BookedDraft {
  return withCarried(
    newBookedDraft(cartId, form.userId, nowMs, { step1: formWithoutCarried(form), scope, rebook, disabledDiscounts: form.disabledDiscounts, ...(form.rejection ? { rejection: form.rejection } : {}) }),
    form.step2,
  );
}

/**
 * The pieces a booking holds, as the choice of step 1 and E11 know them: the line of the delivery is not a piece, and the lines of ONE variant that stand on two rows of the price (the old price and the
 * new one) are one line — folded by `(productInfoId, priceAmount)`, their pieces added, in the order they first stand.
 */
export function foldedLines(model: Pick<EditorOrderModel, 'lines'>): EditorLine[] {
  const folded = new Map<string, EditorLine>();
  model.lines.filter(l => l.type !== 'DELIVERY' && l.cart > 0).forEach(l => {
    const key = `${l.productInfoId}:${l.priceAmount}`;
    const seen = folded.get(key);
    folded.set(key, seen ? { ...seen, cart: seen.cart + l.cart } : l);
  });
  return Array.from(folded.values());
}

/** The booking E12 describes, as a request of E11 («Забронировать заново»): the pieces the booking holds, the time or the address and the slot, the client's number. */
export function rebookRequestOf(model: EditorOrderModel, scope: BookedScope, details?: AddressDetails): BookRequest {
  const lines: LineRef[] = foldedLines(model).map(l => ({ productInfoId: l.productInfoId, priceAmount: l.priceAmount, amount: l.cart }));
  const receiving = model.receiving;
  const address = receiving.address ? withAddressDetails(receiving.address, details) : undefined;
  const slot = receiving.slot;
  const price = receiving.deliveryPrice;
  return {
    brandId: scope.brandId,
    cityId: scope.cityId,
    date: scope.date,
    storeId: model.store.id,
    deliveryType: receiving.type,
    ...(receiving.type === 'PICKUP' && receiving.pickupTime ? { pickupTime: receiving.pickupTime } : {}),
    ...(receiving.type === 'DELIVERY' && address && slot
      ? {
        delivery: {
          rangeId: slot.rangeId,
          address: address.address,
          ...(address.admDiv ? { admDiv: address.admDiv } : {}),
          ...(address.lat !== undefined && address.lng !== undefined ? { lat: address.lat, lng: address.lng } : {}),
          ...(address.entrance ? { entrance: address.entrance } : {}),
          ...(address.floor ? { floor: address.floor } : {}),
          ...(address.flat ? { flat: address.flat } : {}),
          ...(address.note ? { note: address.note } : {}),
          ...(price?.manual ? { manualPriceId: price.priceId } : {}),
        },
      }
      : {}),
    ...(model.client?.phone ? { phone: model.client.phone } : {}),
    ...(model.recipient?.phone ? { recipientPhone: model.recipient.phone } : {}),
    lines,
  };
}

/**
 * The form of step 1 for a booking that has none (it was opened from «Корзины», the tab is new): put together from what the booking holds — the pieces, the way, the store and the time or the
 * address and the slot, the client — so that «Снять бронь» and «Изменить» of «Когда и где» have a form to show. The name of the client is not kept (a number only).
 */
export function step1DraftOfModel(
  model: EditorOrderModel, context: Pick<EditorContextModel, 'countries'> | undefined, userId: number, draftId: string, nowMs: number, details?: AddressDetails,
): OrderDraft {
  const client = partyOfPhone(model.client?.phone, context);
  const recipientParty = model.recipient ? partyOfPhone(model.recipient.phone, context) : undefined;
  const receiving = model.receiving;
  const lines: DraftLine[] = foldedLines(model)
    .map(l => ({
      productInfoId: l.productInfoId, priceAmount: l.priceAmount, amount: l.cart, title: l.title,
      ...(lineWeight(l) ? { weight: lineWeight(l) as number } : {}),
      price: l.priceValue,
    }));
  const base = newDraft(draftId, userId, nowMs);
  return {
    ...base,
    client,
    ...(recipientParty && recipientParty.national ? { recipient: { ...recipientParty, enabled: true } } : {}),
    lines,
    receiving: receiving.type === 'DELIVERY'
      ? {
        type: 'DELIVERY',
        delivery: {
          ...(receiving.address ? { ...withAddressDetails(receiving.address, details) } : {}),
          storeId: model.store.id,
          ...(receiving.slot ? { rangeId: receiving.slot.rangeId } : {}),
          ...(receiving.deliveryPrice?.manual ? { manualPriceId: receiving.deliveryPrice.priceId } : {}),
        },
      }
      : { type: 'PICKUP', storeId: model.store.id, ...(receiving.pickupTime ? { time: receiving.pickupTime } : {}) },
  };
}

/** The number as the form of step 1 holds it: the digits after the dial code and the country; without the countries (not loaded) — nothing, and the form asks for the number again. */
function partyOfPhone(phone: string | undefined, context: Pick<EditorContextModel, 'countries'> | undefined): DraftParty {
  const countries = (context?.countries ?? []).map(toPhoneCountry);
  const digits = phone ?? '';
  const country = countries.find(c => digits.startsWith(c.dialDigits) && digits.length === c.dialDigits.length + c.nationalLength) ?? countries.find(c => digits.startsWith(c.dialDigits));
  return country && digits ? { national: digits.slice(country.dialDigits.length), countryId: country.id } : { national: '' };
}

/**
 * The form of step 1 to bring back when the operator goes back to it from step 2 («Снять бронь», «Изменить» of «Когда и где»): what the booking holds NOW — the pieces (they were changed
 * on step 2), the way, the store and the time or the address and the slot, the client and the recipient — over what only the form knows: the switch «Привязать звонок», the refusal and the
 * choice of the OTHER way (a pickup store the operator looked at while he chose a delivery stays where it was). The discounts switched off are those of step 2, the last ones the operator touched.
 * Without a form (a booking opened from «Корзины») it is the form put together from the booking.
 */
export function step1FormOf(
  model: EditorOrderModel, context: Pick<EditorContextModel, 'countries'> | undefined, snapshot: OrderDraft | undefined, userId: number, draftId: string, nowMs: number,
  disabledDiscounts: readonly DiscountToggle[], details?: AddressDetails, rejection?: RejectionDraft,
): OrderDraft {
  const fresh = step1DraftOfModel(model, context, userId, snapshot?.id ?? draftId, nowMs, details);
  if (!snapshot) {
    return { ...fresh, disabledDiscounts: [...disabledDiscounts], ...(rejection ? { rejection } : {}) };
  }
  const receiving: DraftReceiving = fresh.receiving.type === 'DELIVERY'
    // the delivery is the booking's; the pickup store and time are what the form had
    ? { ...fresh.receiving, ...(snapshot.receiving.storeId !== undefined ? { storeId: snapshot.receiving.storeId } : {}), ...(snapshot.receiving.time ? { time: snapshot.receiving.time } : {}) }
    // the pickup is the booking's; the delivery is what the form had
    : { ...fresh.receiving, ...(snapshot.receiving.delivery ? { delivery: snapshot.receiving.delivery } : {}) };
  // the refusals are the booking's (the newest state of the conversation), not the copy the form was made with
  const { rejection: _stale, ...form } = snapshot;
  return {
    ...form,
    touchedAt: nowMs,
    client: fresh.client.national ? fresh.client : snapshot.client,
    recipient: fresh.recipient.enabled ? fresh.recipient : snapshot.recipient,
    lines: fresh.lines,
    receiving,
    disabledDiscounts: [...disabledDiscounts],
    ...(rejection ? { rejection } : {}),
  };
}

// ---------- storage format ----------

export function serializeBookedDraft(draft: BookedDraft): string {
  return JSON.stringify(draft);
}

type Dict = Record<string, unknown>;

function isDict(value: unknown): value is Dict {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown, max: number): string {
  return typeof value === 'string' ? value.slice(0, max) : '';
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function toggles(value: unknown): DiscountToggle[] {
  const out: DiscountToggle[] = [];
  (Array.isArray(value) ? value : []).forEach(item => {
    const promoId = isDict(item) ? num(item['promoId']) : undefined;
    if (promoId !== undefined && isDict(item)) {
      const priceId = num(item['priceId']);
      out.push({ promoId, ...(priceId !== undefined ? { priceId } : {}), enabled: false });
    }
  });
  return out;
}

function detailsOf(value: unknown): AddressDetails | undefined {
  return isDict(value)
    ? { entrance: str(value['entrance'], DETAIL_LIMIT), floor: str(value['floor'], DETAIL_LIMIT), flat: str(value['flat'], DETAIL_LIMIT), note: str(value['note'], NOTE_LIMIT) }
    : undefined;
}

function scopeOf(value: unknown): BookedScope | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const brandId = num(value['brandId']);
  const cityId = num(value['cityId']);
  const date = typeof value['date'] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value['date']) ? value['date'] : undefined;
  return brandId !== undefined && cityId !== undefined && date ? { brandId, cityId, date } : undefined;
}

/** A request that came back from the storage: only what E11 reads, every piece checked — a stored request is never trusted as it is. */
function rebookOf(value: unknown): BookRequest | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const brandId = num(value['brandId']);
  const cityId = num(value['cityId']);
  const storeId = num(value['storeId']);
  const date = typeof value['date'] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value['date']) ? value['date'] : undefined;
  const lines: LineRef[] = [];
  (Array.isArray(value['lines']) ? value['lines'] : []).forEach(item => {
    if (isDict(item)) {
      const productInfoId = num(item['productInfoId']);
      const priceAmount = num(item['priceAmount']);
      const amount = num(item['amount']);
      if (productInfoId !== undefined && priceAmount !== undefined && priceAmount > 0 && amount !== undefined && Number.isInteger(amount) && amount > 0) {
        lines.push({ productInfoId, priceAmount, amount });
      }
    }
  });
  if (brandId === undefined || cityId === undefined || storeId === undefined || !date || lines.length === 0) {
    return undefined;
  }
  const request: BookRequest = { brandId, cityId, date, storeId, deliveryType: value['deliveryType'] === 'DELIVERY' ? 'DELIVERY' : 'PICKUP', lines };
  const pickupTime = typeof value['pickupTime'] === 'string' && /^\d{2}:\d{2}$/.test(value['pickupTime']) ? value['pickupTime'] : undefined;
  if (pickupTime) {
    request.pickupTime = pickupTime;
  }
  const phone = typeof value['phone'] === 'string' ? value['phone'].replace(/\D/g, '').slice(0, 15) : '';
  if (phone) {
    request.phone = phone;
  }
  const recipientPhone = typeof value['recipientPhone'] === 'string' ? value['recipientPhone'].replace(/\D/g, '').slice(0, 15) : '';
  if (recipientPhone) {
    request.recipientPhone = recipientPhone;
  }
  const delivery = value['delivery'];
  if (isDict(delivery)) {
    const rangeId = num(delivery['rangeId']);
    const address = str(delivery['address'], 200);
    if (rangeId !== undefined && address) {
      const lat = num(delivery['lat']);
      const lng = num(delivery['lng']);
      const manualPriceId = num(delivery['manualPriceId']);
      request.delivery = {
        rangeId, address,
        ...(str(delivery['admDiv'], 200) ? { admDiv: str(delivery['admDiv'], 200) } : {}),
        ...(lat !== undefined && lng !== undefined ? { lat, lng } : {}),
        ...(str(delivery['entrance'], DETAIL_LIMIT) ? { entrance: str(delivery['entrance'], DETAIL_LIMIT) } : {}),
        ...(str(delivery['floor'], DETAIL_LIMIT) ? { floor: str(delivery['floor'], DETAIL_LIMIT) } : {}),
        ...(str(delivery['flat'], DETAIL_LIMIT) ? { flat: str(delivery['flat'], DETAIL_LIMIT) } : {}),
        ...(str(delivery['note'], NOTE_LIMIT) ? { note: str(delivery['note'], NOTE_LIMIT) } : {}),
        ...(manualPriceId !== undefined ? { manualPriceId } : {}),
      };
    }
  }
  return request;
}

/**
 * A draft of an older build, a half-written value or a hand-edited one must never break the page: anything that does not read as a draft of THIS version is «no draft». Every field is checked,
 * cut to its limit and given its default.
 */
export function parseBookedDraft(json: string | null | undefined): BookedDraft | undefined {
  if (!json) {
    return undefined;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (!isDict(raw) || raw['version'] !== BOOKED_DRAFT_VERSION) {
    return undefined;
  }
  const cartId = num(raw['cartId']);
  const userId = num(raw['userId']);
  const touchedAt = num(raw['touchedAt']);
  if (cartId === undefined || userId === undefined || touchedAt === undefined) {
    return undefined;
  }
  const step1 = parseDraft(isDict(raw['step1']) ? JSON.stringify(raw['step1']) : undefined);
  const scope = scopeOf(raw['scope']);
  const rebook = rebookOf(raw['rebook']);
  const addressDetails = detailsOf(raw['addressDetails']);
  const referrer = str(raw['referrer'], REFERRER_LIMIT);
  const company = isDict(raw['company']) ? cleanCompanyDraft(raw['company']) : undefined;
  const rejection = cleanRejectionDraft(raw['rejection']);
  const channel: EditorChannel = raw['channel'] === 'WHATSAPP' || raw['channel'] === 'INSTAGRAM' ? raw['channel'] : 'CALLCENTER';
  return {
    version: BOOKED_DRAFT_VERSION,
    cartId,
    userId,
    touchedAt,
    ...(step1 ? { step1 } : {}),
    ...(scope ? { scope } : {}),
    ...(rebook ? { rebook } : {}),
    ...(addressDetails ? { addressDetails } : {}),
    decorText: str(raw['decorText'], TEXT_LIMIT),
    requestDecor: raw['requestDecor'] === true,
    note: str(raw['note'], TEXT_LIMIT),
    comment: str(raw['comment'], TEXT_LIMIT),
    channel,
    ...(referrer ? { referrer } : {}),
    attachReceipt: raw['attachReceipt'] !== false,
    important: raw['important'] === true,
    kaspiNow: raw['kaspiNow'] === true,
    debt: raw['debt'] === true,
    clientType: raw['clientType'] === 'LEGAL' ? 'LEGAL' : 'INDIVIDUAL',
    // a legal entity could only be chosen (the draft that is older than the mark knew no other way to it)
    ...(raw['clientTypeSet'] === true || raw['clientType'] === 'LEGAL' ? { clientTypeSet: true as const } : {}),
    disabledDiscounts: toggles(raw['disabledDiscounts']),
    ...(company ? { company } : {}),
    ...(rejection ? { rejection } : {}),
    ...(raw['companyWas'] === true ? { companyWas: true as const } : {}),
  };
}

// ---------- what the sweeps of the store need to know of any draft ----------

/** When a stored draft of ANY kind was last touched, or `undefined` for what is not a draft at all. */
export function touchedAtOf(json: string | null | undefined): number | undefined {
  if (!json) {
    return undefined;
  }
  try {
    const raw: unknown = JSON.parse(json);
    return isDict(raw) ? num(raw['touchedAt']) : undefined;
  } catch {
    return undefined;
  }
}

